/** Hydrate authored option controls and publish the matching product variant. */
if (!customElements.get("ai-variant-selector")) {
  customElements.define("ai-variant-selector", class VariantSelector extends HTMLElement {
    connectedCallback() {
      const product = this.closest("ai-product-detail");
      try {
        this._variants = JSON.parse(product?.querySelector("[data-product-variants]")?.textContent || "[]");
      } catch {
        this._variants = [];
      }
      this._groups = Array.from(this.querySelectorAll("[data-product-option]"));
      this._boundClick ||= event => {
        const button = event.target.closest('button[data-product-option-value]');
        if (!button || button.closest("ai-variant-selector") !== this || button.disabled) return;
        const group = button.closest("[data-product-option]");
        for (const control of group.querySelectorAll('button[data-product-option-value]')) {
          control.setAttribute("aria-pressed", String(control === button));
        }
        this._publishSelection();
      };
      this._boundChange ||= event => {
        if (event.target.closest("ai-variant-selector") !== this) return;
        if (event.target.matches('input[data-product-option-value], select[data-product-option-values]')) this._publishSelection();
      };
      this.addEventListener("click", this._boundClick);
      this.addEventListener("change", this._boundChange);
      this._syncInitialSelection();
    }

    /** Some storefront contexts never mark a server-rendered option value as
     * selected. If a group has no pressed control on connect, fall back to
     * the first product variant (always index 0 in the hidden JSON) and
     * mark the matching buttons as selected so the UI never opens blank. */
    _syncInitialSelection() {
      const defaultVariant = this._variants[0];
      if (!defaultVariant || !Array.isArray(defaultVariant.options)) return;
      this._groups.forEach((group, groupIndex) => {
        const controls = Array.from(group.querySelectorAll('button[data-product-option-value]'));
        if (!controls.length) return;
        const alreadyPressed = controls.some(control => control.getAttribute("aria-pressed") === "true");
        if (alreadyPressed) return;
        const targetValue = defaultVariant.options[groupIndex];
        if (targetValue == null) return;
        controls.forEach(control => {
          control.setAttribute("aria-pressed", String(control.getAttribute("data-product-option-value") === String(targetValue)));
        });
      });
    }

    disconnectedCallback() {
      this.removeEventListener("click", this._boundClick);
      this.removeEventListener("change", this._boundChange);
    }

    _publishSelection() {
      const selected = this._groups.map(group => {
        const controls = Array.from(group.querySelectorAll("[data-product-option-value]"));
        const choice = controls.find(control => control.matches('button[aria-pressed="true"], input:checked, option:checked'));
        return choice?.getAttribute("data-product-option-value");
      });
      const variant = this._variants.find(candidate => candidate.options?.length === selected.length
        && candidate.options.every((value, index) => String(value) === selected[index])) || null;
      this.setAttribute("selected-variant-id", variant ? String(variant.id) : "");
      this.dispatchEvent(new CustomEvent("variant:change", {
        bubbles: true,
        detail: { variantId: variant?.id ?? null, variant, available: Boolean(variant?.available) },
      }));
    }
  });
}
